import update from "immutability-helper";
import { ResponseActionTypes, ResponsesAction, ResponseVersionsActionTypes, ResponseVersionsAction } from "./Actions";
import { IFilter, IResponsesState, IResponseVersionsState } from "./Models";

const initialState: IResponsesState = {
  failed: false,
  fetched: false,
  fetching: false,
  error: '',
  responses: {
    firstRowOnPage: 0,
    lastRowOnPage: 10,
    pageCount: 0,
    pageSize: 10,
    rowCount: 0,
    currentPage: 1,
    sortOrder: '',
    results: []
  },
  orderBy: 'dateModified desc',
  filters: [], // Array of filter objects like Analytics
  filteredResults: [],
};

const initialVersionsState: IResponseVersionsState = {
  failed: false,
  fetched: false,
  fetching: false,
  error: '',
  responses: {
    versions: [],
    resourceNotFound: false,
    errors: [],
    hasErrors: false
  }
};

// Helper function to update filters array (same pattern as Analytics)
const updateFilters = (filters: IFilter[], field: string, value: any, operator: string = 'eq'): IFilter[] => {
  const updatedFilters = JSON.parse(JSON.stringify(filters));
  const index = filters.findIndex((obj: IFilter) => obj.field === field && obj.operator === operator);

  if (index > -1) {
    if (value !== undefined && value !== null && value !== '') {
      updatedFilters[index].value = value;
    } else {
      updatedFilters.splice(index, 1);
    }
  } else if (value !== undefined && value !== null && value !== '') {
    updatedFilters.push({ field, value, operator });
  }

  return updatedFilters;
};

// Helper function to remove all filters for a field
const removeFiltersForField = (filters: IFilter[], field: string): IFilter[] => {
  return filters.filter((obj: IFilter) => obj.field !== field);
};

export const ResponsesReducer = (
  state = initialState,
  action: ResponsesAction
): IResponsesState => {
  switch (action.type) {
    case ResponseActionTypes.FetchResponsesPending:
      return update(state, {
        fetched: { $set: false },
        fetching: { $set: true },
        failed: { $set: false },
        error: { $set: '' }
      });
    case ResponseActionTypes.FetchResponsesRejected:
      return update(state, {
        responses: { $set: { ...initialState.responses } },
        error: { $set: action.payload.error },
        fetched: { $set: false },
        fetching: { $set: false },
        failed: { $set: true }
      });
    case ResponseActionTypes.FetchedResponses:
      return update(state, {
        fetched: { $set: true },
        fetching: { $set: false },
        failed: { $set: false },
        error: { $set: '' },
        responses: { $set: { ...action.payload.responses } },
        filteredResults: { $set: action.payload.responses.results || [] }
      });
    case ResponseActionTypes.SetFilter:
      // Handle filter value - could be simple value or object with from/to for ranges
      const { field, value } = action.payload;
      let newFilters = [...state.filters];

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Handle range filters (from/to for dates, min/max for numbers)
        if ('from' in value || 'to' in value) {
          // Date range filter
          if (value.from) {
            newFilters = updateFilters(newFilters, field, value.from.value, value.from.operator);
          }
          if (value.to) {
            newFilters = updateFilters(newFilters, field, value.to.value, value.to.operator);
          }
        } else {
          // Simple object value
          newFilters = updateFilters(newFilters, field, value);
        }
      } else {
        // Simple value or array
        newFilters = updateFilters(newFilters, field, value);
      }
      return update(state, {
        filters: { $set: newFilters },
        // No client-side filtering, just pass through the original results
        filteredResults: { $set: state.responses.results || [] }
      });
    case ResponseActionTypes.ClearFilter:
      return update(state, {
        filters: { $set: removeFiltersForField(state.filters, action.payload.field) },
        // No client-side filtering, just pass through the original results
        filteredResults: { $set: state.responses.results || [] }
      });
    case ResponseActionTypes.ClearAllFilters:
      return update(state, {
        filters: { $set: [] },
        // No client-side filtering, just pass through the original results
        filteredResults: { $set: state.responses.results || [] }
      });
    case ResponseActionTypes.SetSortOrder:
      return update(state, {
        orderBy: { $set: action.payload.orderBy }
      });
    case ResponseActionTypes.ResetSortOrder:
      return update(state, {
        orderBy: { $set: initialState.orderBy }
      });
    default:
      return state;
  }
}

export const ResponseVersionsReducer = (
  state = initialVersionsState,
  action: ResponseVersionsAction
): IResponseVersionsState => {
  switch (action.type) {
    case ResponseVersionsActionTypes.FetchResponseVersionsPending:
      return update(state, {
        fetched: { $set: false },
        fetching: { $set: true },
        failed: { $set: false },
        error: { $set: '' }
      });
    case ResponseVersionsActionTypes.FetchResponseVersionsRejected:
      return update(state, {
        responses: { $set: initialVersionsState.responses },
        error: { $set: action.payload.error },
        fetched: { $set: false },
        fetching: { $set: false },
        failed: { $set: true }
      });
    case ResponseVersionsActionTypes.FetchedResponseVersions:
      return update(state, {
        fetched: { $set: true },
        fetching: { $set: false },
        failed: { $set: false },
        error: { $set: '' },
        responses: { $set: action.payload.responses }
      });
    case ResponseVersionsActionTypes.ResetResponseVersions:
      return initialVersionsState;
    default:
      return state;
  }
}
